import axios from 'axios';
import moment from 'moment';
import React, { Component } from 'react';
import { isMobile } from "react-device-detect";
import { Provider } from 'react-redux';
import ReduxToastr from 'react-redux-toastr';
import { Spinner } from 'react-redux-spinner';
import LogoutWarning from '../../../common/components/LogoutWarning';
import { updateToken } from "../../store/actions";
import {
    setSignalRConnection,
    updateBuildInfos,
    updateDateRange,
    updateEmployeeDashboardData,
    updateFilterList,
    updateGlobalConfigs,
    updateLoading,
    setManagerSettings,
    appendManagerSettings,
    updatePerson,
    updateRefreshedDates,
    updateSnackbar,
    updateTimeOffRequestSubtypes,
} from "../actions";
import * as Net from './Utils/dotNet';
// Component imports
import { uniq } from 'lodash';
import * as aux from '../../auxilaries';
import { RequestTypeColors, RequestTypes, ShiftSegmentTypeName } from '../../Enums';
import * as Utils from '../../utils';
import { currentHttpOptions, MomentFormats } from '../../utils';
import DesktopBillboard from './Desktop/index';
import MobileBillboard from './Mobile/index';
import { getFirstDayOfWeek, getMonthWeekRange } from './Utils/momentHelper';
import { getLastSegment, removeLastSegment } from './Utils/pathHelper';
import * as UI from './Utils/uiGenerators';
import { loadShiftsAndPaycodes } from './Utils/loadShiftsAndPaycodes';

import { withTranslation } from 'react-i18next';
import { DateFnsFormats, formatAs } from '../../../common-client2/src/services/ukgDateTime';
import createLiveUpdateConnection from '../../services/createLiveUpdateConnection';
import { employeeDashboardStore as store } from '../../store';

class EmployeeDashboard extends Component {
    calendarRef = React.createRef();
    constructor(props) {
        super(props);
        this.state = {
            openShiftEnabled: true,
            managerSettingsLoaded: false,
            shiftsLoaded: false,
            firstDayOfTheWeek: getFirstDayOfWeek(),
        };
        document.title = this.props.t('settings.pageTitle.employee');
        this.getInitData();
    }

    componentDidMount() {
        const { signalRConnection } = store.getState();

        if (!signalRConnection) {
            const connection = createLiveUpdateConnection('hub');
            connection.start()
                .then(() => store.dispatch(setSignalRConnection(connection)))
                .catch(err => console.log('SignalR Connection Error:', err));
        }
    }

    componentWillUnmount() {
        const { signalRConnection } = store.getState();
        if (signalRConnection) {
            signalRConnection.stop();
            store.dispatch(setSignalRConnection(null));
        }
    }
    
    /**
     * Load all request-related data (offers, swaps, VTO, etc.) for the current date range
     * This function is called both on initial load and when the month changes
     * @param {boolean} fetchPrivate - Whether to fetch private offers
     * @param {Promise} managerSettingsPromise - Optional promise to wait for before calling APIs that need manager settings
     * @returns {Promise<Object>} - Object containing all API responses
     */
    loadRequestData = async (fetchPrivate = false, managerSettingsPromise = null) => {
        const { openShiftEnabled } = this.state;
        const { dateRanges } = store.getState();

        await store.dispatch(updateToken());

        // APIs that don't need manager settings - can run immediately
        let coreRequests = [
            Net.getMyShiftOffersToCover(currentHttpOptions(), dateRanges.startDate, dateRanges.endDate),
            Net.getMyShiftRequestsToCover(currentHttpOptions(), dateRanges.startDate, dateRanges.endDate),
            Net.getAvailableShiftToCoverOffers(currentHttpOptions(), dateRanges.startDate, dateRanges.endDate, 1),
            Net.getMyShiftSwapOffers(currentHttpOptions(), dateRanges.startDate, dateRanges.endDate),
            Net.getAvailableShiftToSwapOffers(currentHttpOptions(), dateRanges.startDate, dateRanges.endDate),
            Net.getMySwapProposals(currentHttpOptions(), dateRanges.startDate, dateRanges.endDate),
            Net.getMyGlobalHistory(currentHttpOptions(), dateRanges.startDate, dateRanges.endDate),
            Net.getAvailableVTO(currentHttpOptions(), dateRanges.startDate, dateRanges.endDate),
            Net.getMyVTORequest(currentHttpOptions(), dateRanges.startDate, dateRanges.endDate),
        ];

        // APIs that need manager settings - wait for promise if provided
        let openShiftRequests = [];
        if (openShiftEnabled) {
            // Wait for manager settings if promise provided
            if (managerSettingsPromise) {
                await managerSettingsPromise;
            }

            // Now get manager settings from store (will be populated if promise was provided)
            const { managerSettings } = store.getState();

            openShiftRequests = [
                Net.getAvailableOpenShift(currentHttpOptions(), dateRanges.startDate, dateRanges.endDate, managerSettings.filter(setting => setting.type === 2 && setting.isVisible).map(setting => setting.id)),
                Net.getMyOpenShiftRequests(currentHttpOptions(), dateRanges.startDate, dateRanges.endDate)
            ];
        }

        let privateRequest = [];
        if (fetchPrivate) {
            privateRequest = [Net.getPrivateOffers(currentHttpOptions())];
        }

        // Combine all requests and execute in parallel
        const allRequests = [...coreRequests, ...openShiftRequests, ...privateRequest];
        const allResponses = await axios.all(allRequests);

        const [
            employeeShiftOffersResponse,
            employeeShiftRequestsResponse,
            availableShiftToCoverOffersResponse,
            employeeMyShiftSwapOffersResponse,
            availableShiftToSwapOffersResponse,
            mySwapProposalsResponse,
            myGlobalHistoryResponse,
            availableVTOResponse,
            myVTORequestsResponse,
            ...optionalResponses
        ] = allResponses;

        // Build parameters array based on what was requested
        const parameters = {
            employeeShiftOffersResponse,
            employeeShiftRequestsResponse,
            availableShiftToCoverOffersResponse,
            employeeMyShiftSwapOffersResponse,
            availableShiftToSwapOffersResponse,
            mySwapProposalsResponse,
            myGlobalHistoryResponse,
            availableVTOResponse,
            myVTORequestsResponse,
            availableOpenShiftsResponse: null,
            myOpenShiftRequestsResponse: null,
            privateOffersResponse: null
        };

        // Map optional responses based on what was requested
        let optionalIndex = 0;
        if (openShiftEnabled) {
            parameters.availableOpenShiftsResponse = optionalResponses[optionalIndex++];
            parameters.myOpenShiftRequestsResponse = optionalResponses[optionalIndex++];
        }
        if (fetchPrivate) {
            parameters.privateOffersResponse = optionalResponses[optionalIndex++];
        }

        return parameters;
    }

    /**
     * Load all month-specific data (shifts, paycodes, and requests)
     * This function is called both on initial load and when the month changes
     * @param {boolean} fetchPrivate - Whether to fetch private offers
     * @param {Promise} managerSettingsPromise - Optional promise to wait for before processing data
     */
    loadMonthData = async (fetchPrivate = false, managerSettingsPromise = null) => {
        store.dispatch(updateLoading(true));
        try {
            // Load shifts and paycodes in parallel with request data.
            // Set shiftsLoaded as soon as shifts complete so mobile can render them
            // before requests finish loading.
            const shiftsPromise = loadShiftsAndPaycodes(store.dispatch, store.getState())
                .then(success => {
                    if (success) {
                        this.setState({ shiftsLoaded: true });
                    }
                    return success;
                });

            const [, requestData] = await Promise.all([
                shiftsPromise,
                this.loadRequestData(fetchPrivate, managerSettingsPromise)
            ]);

            // Process request data and update calendar
            // Note: loadRequestData already waited for managerSettingsPromise if needed
            await this.refreshDataSuccessCallback(
                requestData.employeeShiftOffersResponse,
                requestData.employeeShiftRequestsResponse,
                requestData.availableShiftToCoverOffersResponse,
                requestData.employeeMyShiftSwapOffersResponse,
                requestData.availableShiftToSwapOffersResponse,
                requestData.mySwapProposalsResponse,
                requestData.myGlobalHistoryResponse,
                requestData.availableVTOResponse,
                requestData.myVTORequestsResponse,
                requestData.availableOpenShiftsResponse,
                requestData.myOpenShiftRequestsResponse,
                requestData.privateOffersResponse
            );
        } catch (err) {
            console.log('Error loading month data:', err);
            let message = aux.handleAPIErrors(err.response);
            store.dispatch(updateSnackbar({
                typeSnackbar: 'error',
                messageSnackbar: message,
                openSnackbar: true,
            }));
        } finally {
            store.dispatch(updateLoading(false));
        }
    }

    getInitData =  async () => {
        const { firstDayOfTheWeek } = this.state;
        const now = moment.utc();
        const { startDate, endDate } = getMonthWeekRange(now, firstDayOfTheWeek);

        store.dispatch(updateDateRange({
            startDate: startDate.format(Utils.MomentFormats.YMD),
            endDate: endDate.format(Utils.MomentFormats.YMD)
        }));

        var userTokenInfos = JSON.parse(sessionStorage.getItem("jwtUserInfo"));
        store.dispatch(updatePerson({
            firstName: userTokenInfos?.FirstName,
            lastName: userTokenInfos?.LastName,
            personName: sessionStorage.getItem("FullName"),
            personNumber: sessionStorage.getItem("PersonNumber"),
            currentEmployeeId: sessionStorage.getItem("UserId") ? parseInt(sessionStorage.getItem("UserId")) : 0,
            primaryJob: sessionStorage.getItem("PrimaryJob"),
        }));

        const { dateRanges } = store.getState();

        // Start loading one-time data in parallel (don't await)
        Net.getTimeOffRequestSubtypes(currentHttpOptions()).then(async response => {
            await store.dispatch(updateTimeOffRequestSubtypes(
                [
                    {
                        id: null,
                        name: "None",
                        reasons: [],
                    },
                    ...response.data
                ]));
        });

        Net.getBuildInfos(currentHttpOptions())
            .then(async buildInfos => {
                await store.dispatch(updateBuildInfos({ ...buildInfos.data }));
            });

        Net.getGlobalConfigs(currentHttpOptions())
            .then(async globalConfigsResponse => {
                await store.dispatch(updateGlobalConfigs({ ...globalConfigsResponse.data, isLoaded: true }));
            });

        // Load manager settings in parallel with month data
        store.dispatch(updateLoading(true));
        const managerSettingsPromise = Net.getManagerSettings(dateRanges.startDate, dateRanges.endDate, currentHttpOptions())
            .then(async (managerSettingsResponse) => {
                let managerSettings = managerSettingsResponse.data;
                await store.dispatch(setManagerSettings(managerSettings));

                this.setState({
                    managerSettingsLoaded: true
                });
            })
            .catch(err => {
                let message = aux.handleAPIErrors(err.response);
                store.dispatch(updateSnackbar({
                    typeSnackbar: 'error',
                    messageSnackbar: message,
                    openSnackbar: true,
                }));
            })
            .finally(() => {
                store.dispatch(updateLoading(false));
            });

        // Load month-specific data (shifts, paycodes, and all requests) in parallel with manager settings
        // Pass managerSettingsPromise so loadMonthData waits for it before calling refreshDataSuccessCallback
        // fetchPrivate=true on initial load to get private offers
        await this.loadMonthData(true, managerSettingsPromise);
    }

    // Helper method to collect unique manager setting IDs from API responses
    collectManagerSettingIds = (employeeShiftOffersResponse,
            employeeShiftRequestsResponse,
            availableShiftToCoverOffersResponse,
            employeeMyShiftSwapOffersResponse,
            availableShiftToSwapOffersResponse,
            mySwapProposalsResponse,
            myGlobalHistoryResponse,
            availableVTOResponse,
            myVTORequestsResponse,
            availableOpenShiftsResponse,
            myOpenShiftRequestsResponse,
            privateOffersResponse,
            managerSettings) => {
        const managerSettingIds = new Set();
        let responses = [
            employeeShiftOffersResponse,
            employeeShiftRequestsResponse,
            availableShiftToCoverOffersResponse,
            employeeMyShiftSwapOffersResponse,
            availableShiftToSwapOffersResponse,
            mySwapProposalsResponse,
            myGlobalHistoryResponse,
            availableVTOResponse,
            myVTORequestsResponse,
            availableOpenShiftsResponse,
            myOpenShiftRequestsResponse,
            privateOffersResponse,
        ];
        responses.forEach(response => {
            if (response && response.data) {
                if (response.data.managerSettingGuids && Array.isArray(response.data.managerSettingGuids)) {// GetPrivateOffers
                    response.data.managerSettingGuids.forEach(guid => {
                        if (guid) {
                            managerSettingIds.add(guid);
                        }
                    });
                } else{
                    let dataArray = [];
                    // Handle different response structures
                    if (Array.isArray(response.data)) {// Others
                        dataArray = response.data;
                    } else if (response.data.offers && Array.isArray(response.data.offers)) {// TC/GetOffers, SS/GetAvailableOffers
                        dataArray = response.data.offers;
                    } else if (response.data.history && Array.isArray(response.data.history)) {// GetMyGlobalHistory
                        dataArray = response.data.history;
                    }

                    // Extract manager setting IDs from each item
                    dataArray.forEach(item => {
                        if (item.managerSettingAggregateRootId) {
                            managerSettingIds.add(item.managerSettingAggregateRootId);
                        }
                        if (item.ManagerSettingAggregateRootId) {
                            managerSettingIds.add(item.ManagerSettingAggregateRootId);
                        }
                    });
                }
            }
        });
        var managerSettingIdsList = Array.from(managerSettingIds).filter(id => !managerSettings.find(x => x.aggregateRootId === id));
        return managerSettingIdsList;
    }

    refreshDataSuccessCallback = async (employeeShiftOffersResponse, employeeShiftRequestsResponse, availableShiftToCoverOffersResponse, employeeMyShiftSwapOffersResponse, availableShiftToSwapOffersResponse, mySwapProposalsResponse,
        myGlobalHistoryResponse, availableVTOResponse, myVTORequestsResponse, availableOpenShiftsResponse, myOpenShiftRequestsResponse, privateOffersResponse) => {
        const { person, dateRanges, managerSettings, statusDictionary, data, globalConfigs } = store.getState()

        let updatedAvailableOpenShifts;
        let updatedMyOpenShiftRequests;
        let updatedPrivateOffersSummary;
        let updatedPrivateOffers;
        if (availableOpenShiftsResponse) {
            updatedAvailableOpenShifts = availableOpenShiftsResponse.data
        }
        else {
            updatedAvailableOpenShifts = data.availableOpenShifts
        }
        if (myOpenShiftRequestsResponse) {
            updatedMyOpenShiftRequests = myOpenShiftRequestsResponse.data
        } else {
            updatedMyOpenShiftRequests = data.myOpenShiftRequests
        }
        if (privateOffersResponse) {
            let res = privateOffersResponse.data;
            updatedPrivateOffersSummary = {
                new: res.new,
                total: res.total,
                offerIds: res.privateOfferIds,
                loadedIds: []
            }
            updatedPrivateOffers = []
        } else {
            updatedPrivateOffersSummary = data.privateOffersSummary
            updatedPrivateOffers = data.privateOffers
        }

        
        // Collect manager setting IDs from all responses and fetch archived settings
        let managerSettingsWithArchived = [ ...managerSettings ];
        const managerSettingIds = this.collectManagerSettingIds(
            employeeShiftOffersResponse,
            employeeShiftRequestsResponse,
            availableShiftToCoverOffersResponse,
            employeeMyShiftSwapOffersResponse,
            availableShiftToSwapOffersResponse,
            mySwapProposalsResponse,
            myGlobalHistoryResponse,
            availableVTOResponse,
            myVTORequestsResponse,
            availableOpenShiftsResponse,
            myOpenShiftRequestsResponse,
            privateOffersResponse,
            managerSettings,
        );

        // Call archived manager settings with collected IDs if we have any
        if (managerSettingIds.length > 0) {
            await Net.getArchivedManagerSettings(
                dateRanges.startDate,
                dateRanges.endDate,
                currentHttpOptions(),
                managerSettingIds
            ).then(async archivedResponse => {
                // Handle archived manager settings response
                await store.dispatch(appendManagerSettings(archivedResponse.data));
                managerSettingsWithArchived = [...managerSettingsWithArchived, ...archivedResponse.data];
            }).catch(err => {
                console.log('Error fetching archived manager settings:', err);
                let message = aux.handleAPIErrors(err.response);
                store.dispatch(updateSnackbar({
                    typeSnackbar: 'error',
                    messageSnackbar: message,
                    openSnackbar: true,
                }));
            });
        } else {
            console.log('No manager setting IDs found, skipping archived manager settings fetch');
        }

        let availableShiftToSwapOffers = availableShiftToSwapOffersResponse.data.offers; // TODO: Ask what the function of validationDetails
        let monthlySchedule = UI.generateSchedule(true,
            data.employeeShifts,
            employeeShiftOffersResponse.data,
            availableShiftToCoverOffersResponse.data.offers,
            employeeShiftRequestsResponse.data,
            employeeMyShiftSwapOffersResponse.data,
            availableShiftToSwapOffers,
            data.employeePaycodes,
            mySwapProposalsResponse.data,
            updatedAvailableOpenShifts, // TODO: OPEN SHIFT
            myOpenShiftRequestsResponse.data,
            availableVTOResponse.data,
            myVTORequestsResponse.data,
            statusDictionary,
            person.personName,
            managerSettingsWithArchived,
            dateRanges,
            globalConfigs.postedRequestDisplay,
        )

        let payload = {
            monthlySchedule: monthlySchedule,
            //          weeklySchedule: weeklySchedule,
            availableShiftsToCover: availableShiftToCoverOffersResponse.data.offers,
            employeeShiftOffers: employeeShiftOffersResponse.data,
            employeeShiftRequests: employeeShiftRequestsResponse.data,
            employeeMyShiftSwapOffers: employeeMyShiftSwapOffersResponse.data,
            availableShiftToSwapOffers: availableShiftToSwapOffers,
            availableVTO: availableVTOResponse.data,
            myVTORequests: myVTORequestsResponse.data,
            myProposals: mySwapProposalsResponse.data,
            validProposalShifts: [],
            globalHistory: myGlobalHistoryResponse.data.history,
            availableOpenShifts: updatedAvailableOpenShifts,
            myOpenShiftRequests: updatedMyOpenShiftRequests,
            privateOffersSummary: updatedPrivateOffersSummary,
            privateOffers: updatedPrivateOffers,
        }
        store.dispatch(updateEmployeeDashboardData(payload));

        let dates = [];
        for (let offer of availableShiftToCoverOffersResponse.data.offers) {
            let date = offer.isPartial ? formatAs(offer.partialFromDate, DateFnsFormats.YMD) : formatAs(offer.shift.startDateTime, DateFnsFormats.YMD);
            if (dates[date]) {
                dates[date]++;
            } else {
                dates[date] = 1;
            }
        }
        let datesNotToRefresh = [];
        let currentDate = moment(dateRanges.startDate);
        let endDate = moment(dateRanges.endDate);
        while (currentDate <= endDate) {
            let nbTcOffersInDate = dates[currentDate.format(MomentFormats.YMD)];

            if (!nbTcOffersInDate || nbTcOffersInDate < 4) {
                datesNotToRefresh.push(currentDate.format(MomentFormats.YMD));
            }
            currentDate = currentDate.add(1, 'day');
        }

        store.dispatch(updateRefreshedDates(datesNotToRefresh));

        this.generateCalendarDefinitionAndFilters(managerSettingsWithArchived, monthlySchedule);
    }


    /**
     * Refresh data for the current month
     * This is called when the month changes in the calendar
     * @param {boolean} fetchPrivate - Whether to fetch private offers
     * @param {Promise} managerSettingsPromise - Optional promise to wait for before processing data
     */
    refreshData = async (fetchPrivate = false, managerSettingsPromise = null) => {
        await this.loadMonthData(fetchPrivate, managerSettingsPromise);
    }

    handleLoadToCoverOffers = async (dates, spinner = true) => {
        const { person, dateRanges, managerSettings, statusDictionary, refreshedDates, data, globalConfigs } = store.getState();
        let errorMsg;

        if (dates && dates.length) {
            dates = uniq(dates.filter(x => !refreshedDates.includes(x)));
            let axiosRequests = []

            if (dates && dates.length) {
                for (let date of dates) {
                    axiosRequests.push(Net.getAvailableShiftToCoverOffers(currentHttpOptions(), date, date, 1))
                }

                if (spinner) {
                    store.dispatch(updateLoading(true));
                }

                await axios.all(axiosRequests).then(async responses => {
                    let hasErrors = false
                     
                    let message = responses.map(response => {
                        let errorMessage = aux.handleAPIErrors(response)
                        if (errorMessage) {
                            hasErrors = true
                        }
                    })
                    if (hasErrors) {
                        errorMsg = message;
                    } else {
                        let toCoversToAdd = [];
                        for (let res of responses) {
                            if (res.data.offers && res.data.offers.length) {
                                toCoversToAdd.push(...res.data.offers);
                            }
                        }

                        let availableShiftsToCover = data.availableShiftsToCover;
                        let availableShiftsToCoverIds = availableShiftsToCover.map(x => x.offerId);

                        toCoversToAdd = toCoversToAdd.filter(x => !availableShiftsToCoverIds.includes(x.offerId))
                        if (toCoversToAdd.length) {
                            availableShiftsToCover = [...availableShiftsToCover, ...toCoversToAdd];

                            let monthlySchedule = UI.generateSchedule(
                                true,
                                data.employeeShifts,
                                data.employeeShiftOffers,
                                availableShiftsToCover,
                                data.employeeShiftRequests,
                                data.employeeMyShiftSwapOffers,
                                data.availableShiftToSwapOffers,
                                data.employeePaycodes,
                                data.myProposals,
                                data.availableOpenShifts, // TODO: OPEN SHIFT
                                data.myOpenShiftRequests,
                                data.availableVTO,
                                data.myVTORequests,
                                statusDictionary,
                                person.personName,
                                managerSettings,
                                dateRanges,
                                globalConfigs.postedRequestDisplay,
                            )

                            store.dispatch(updateEmployeeDashboardData({ ...data, monthlySchedule: monthlySchedule, availableShiftsToCover: availableShiftsToCover }));
                        }
                        store.dispatch(updateRefreshedDates([...refreshedDates, ...dates]));
                    }

                }).catch(err => {
                    errorMsg = aux.handleAPIErrors(err.response)
                }).finally(() => {
                    if (spinner) {
                        store.dispatch(updateLoading(false));
                    }
                });
            }
        }
        return errorMsg;
    }


    getLocationFilters = (monthlySchedule = []) => {
        const allLocations = monthlySchedule.map((item) => {
            const shiftLocations = item.raw.segments?.filter(x => x.type !== ShiftSegmentTypeName.BREAK_SEGMENT).map((segment) => segment.orgJobRef.qualifier) || [];
            const toCoverLocations = item.raw.shift?.segments?.filter(x => x.type !== ShiftSegmentTypeName.BREAK_SEGMENT).map((segment) => segment.orgJobRef.qualifier) || [];
            const otherLocations = item.raw.shifts?.flatMap((shift) => shift.segments?.filter(x => x.type !== ShiftSegmentTypeName.BREAK_SEGMENT).flatMap((segment) => segment.orgJobRef.qualifier)) || [];
            return [...shiftLocations, ...toCoverLocations, ...otherLocations];
        });

        const allUniqueLocations = [...new Set(allLocations.flat())];
        const allUniqueLocationsOnly = allUniqueLocations.map((location) => removeLastSegment(location));

        return [...new Set(allUniqueLocationsOnly)].sort().map((value, index) => ({ id: index, name: value, checked: true }));;
    }

    getJobFilters = (monthlySchedule = []) => {
        const allLocations = monthlySchedule.map((item) => {
            const shiftLocations = item.raw.segments?.filter(x => x.type !== ShiftSegmentTypeName.BREAK_SEGMENT).map((segment) => segment.orgJobRef.qualifier) || [];
            const toCoverLocations = item.raw.shift?.segments?.filter(x => x.type !== ShiftSegmentTypeName.BREAK_SEGMENT).map((segment) => segment.orgJobRef.qualifier) || [];
            const otherLocations = item.raw.shifts?.flatMap((shift) => shift.segments?.filter(x => x.type !== ShiftSegmentTypeName.BREAK_SEGMENT).flatMap((segment) => segment.orgJobRef.qualifier)) || [];
            return [...shiftLocations, ...toCoverLocations, ...otherLocations];
        });
        
        const allUniqueLocations = [...new Set(allLocations.flat())];
        const allUniqueJobsOnly = allUniqueLocations.map((location) => getLastSegment(location));
        
        return [...new Set(allUniqueJobsOnly)].sort().map((value, index) => ({ id: index, name: value, checked: true }));
    }
    
    generateCalendarDefinitionAndFilters = (managerSettings, monthlySchedule) => {
        let calendarDefinition = [], filterDefinition = [], availableCalendarDefinition = [], availableFilterDefinition = [];
        let skipValidationOffers = {
            filters: [],
        };
        managerSettings = managerSettings.sort((a, b) => a.type - b.type);

        /** @typedef {typeof RequestTypeColors} IRequestTypeColors */

        for (let managerSetting of managerSettings) {

            /** @type {IRequestTypeColors[keyof IRequestTypeColors] | "#8A21E6"} */
            let borderColor;
            /** @type {string} */
            let label;
            switch (managerSetting.type) {
                case RequestTypes.toCover: // To Cover
                    borderColor = RequestTypeColors.toCover;
                    label = `Available ${managerSetting.name}`;
                    break;
                case RequestTypes.shiftSwap: // Shift Swap
                    borderColor = RequestTypeColors.shiftSwap;
                    label = `Available ${managerSetting.name}`;
                    break;
                case RequestTypes.openShift: // Open shift
                    borderColor = RequestTypeColors.openShift;
                    label = `Open shift ${managerSetting.name}`;
                    break;
                case RequestTypes.voluntaryTimeOff: // Voluntary Time Off
                    borderColor = RequestTypeColors.voluntaryTimeOff;
                    label = `Available ${managerSetting.name}`;
                    break;
                default:
                    borderColor = '#8A21E6';
            }
            let calendarId = managerSetting.id + 1000000000;
            let isSkipValidationOffer = monthlySchedule.find(x => x.isSkipValidation && x.managerSettingId === managerSetting.id);
            let availableId = managerSetting.id + 2000000000;
            if (managerSetting.isVisible || managerSetting.isArchive || !managerSetting.isInHyperfind) {
                let managerSettingsId = managerSettings.filter(x => x.aggregateRootId === managerSetting.aggregateRootId).map(x => x.id.toString());

                filterDefinition.push({
                    id: calendarId,
                    name: managerSetting.name,
                    bgColor: '#FFFFFF',
                    borderColor: '#8A21E6',
                    checked: true,
                    calendarIds: managerSettingsId,
                    parentCategory: 1
                })
                availableFilterDefinition.push({
                    id: availableId,
                    name: label,
                    bgColor: '#FFFFFF',
                    borderColor: borderColor,
                    checked: true,
                    calendarIds: managerSettingsId,
                    parentCategory: 0
                })
                if(isSkipValidationOffer){
                    skipValidationOffers.filters.push({
                        id: managerSetting.id + 3000000000 ,
                        name: managerSetting.name,
                        bgColor: '#FFFFFF',
                        borderColor: borderColor,
                        checked: false,
                        calendarIds: managerSettingsId,
                        parentCategory: 3
                    }) 
                }
            }
            calendarDefinition.push({
                id: calendarId.toString(),
                name: managerSetting.name,
                bgColor: '#FFFFFF',
                borderColor: '#8A21E6',
            })

            availableCalendarDefinition.push({
                id: availableId.toString(),
                name: label,
                bgColor: '#FFFFFF',
                borderColor: borderColor,
            })
        }
        const locationFilterDefinition = {
            filters: this.getLocationFilters(monthlySchedule),
            isTransferIncluded: true
        };

        const jobFilterDefinition = {
            filters: this.getJobFilters(monthlySchedule),
            isTransferIncluded: true
        };

        store.dispatch(updateFilterList({
            filterDefinition,
            calendarDefinition,
            availableFilterDefinition,
            availableCalendarDefinition,
            locationFilterDefinition,
            jobFilterDefinition,
            skipValidationOffers
        }));
    }

    render() {
        return (
            <Provider store={store}>
                <Spinner config={{ easing: 'ease', speed: 1000 }} />
                {
                    isMobile ? (
                        <MobileBillboard
                            managerSettingsLoaded={this.state.managerSettingsLoaded}
                            shiftsLoaded={this.state.shiftsLoaded}
                            refreshData={this.refreshData}
                            firstDayOfTheWeek={this.state.firstDayOfTheWeek}
                            generateCalendarDefinitionAndFilters={this.generateCalendarDefinitionAndFilters}
                            handleLoadToCoverOffers={this.handleLoadToCoverOffers}
                        />
                    ) : (
                        <DesktopBillboard
                            // TODO: data props to pass
                            refreshData={this.refreshData}
                            managerSettingsLoaded={this.state.managerSettingsLoaded}
                            shiftsLoaded={this.state.shiftsLoaded}
                            firstDayOfTheWeek={this.state.firstDayOfTheWeek}
                            generateCalendarDefinitionAndFilters={this.generateCalendarDefinitionAndFilters}
                            handleLoadToCoverOffers={this.handleLoadToCoverOffers}
                        />
                    )
                }
                <ReduxToastr
                    timeOut={4000}
                    newestOnTop={false}
                    preventDuplicates
                    position="top-center"
                    transitionIn="fadeIn"
                    transitionOut="fadeOut"
                    closeOnToastrClick
                />
                <LogoutWarning />
            </Provider>
        )
    }
}

export default withTranslation('employmentBillboard')(EmployeeDashboard);
